import { inject, Injectable } from '@angular/core';
import { BackOfficeHttpService } from '@core/http/back-office.http.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FileHandlerService } from '@core/services/file-handler.service';
import * as backOfficeActions from '@state/back-office/back-office.actions';
import { parseError } from '@state/errors.parser';
import { combineLatest, of, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { environment } from '@environment';

@Injectable()
export class BackOfficeEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(BackOfficeHttpService);
  private readonly fileHandlerService = inject(FileHandlerService);

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getRoles),
      switchMap(() => {
        return this.http.getRoles().pipe(
          map(roles => {
            return backOfficeActions.getRolesSuccess({ roles });
          }),
          catchError(error => {
            const errorMessage = parseError(error, backOfficeActions.getRoles.type);
            return of(backOfficeActions.getRolesError({ errorMessage }));
          })
        );
      })
    )
  );

  getLanguages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getLanguages),
      switchMap(() => {
        return this.http.getLanguages().pipe(
          map(languages => {
            return backOfficeActions.getLanguagesSuccess({ languages });
          }),
          catchError(error => {
            const errorMessage = parseError(error, backOfficeActions.getLanguages.type);
            return of(backOfficeActions.getLanguagesError({ errorMessage }));
          })
        );
      })
    )
  );

  getCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getCompanies),
      switchMap(() => {
        return this.http.getCompanies().pipe(
          map(companies => {
            return backOfficeActions.getCompaniesSuccess({ companies });
          }),
          catchError(error => {
            const errorMessage = parseError(error, backOfficeActions.getCompanies.type);
            return of(backOfficeActions.getCompaniesError({ errorMessage }));
          })
        );
      })
    )
  );

  getEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getEmployees),
      switchMap(() => {
        return this.http.getEmployees().pipe(
          map(employees => {
            return backOfficeActions.getEmployeesSuccess({ employees });
          }),
          catchError(error => {
            const errorMessage = parseError(error, backOfficeActions.getEmployees.type);
            return of(backOfficeActions.getEmployeesError({ errorMessage }));
          })
        );
      })
    )
  );

  getPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getPartners),
      switchMap(({ payload }) => {
        const request$ = this.http.getPartners(payload);
        const delay$ = timer((environment.minLoadingDataTime || 0.5) * 1000);
        return combineLatest([request$, delay$]).pipe(
          map(([partners]) => {
            return backOfficeActions.getPartnersSuccess({ partners: partners });
          }),
          catchError(error => {
            const errorMessage = parseError(error, backOfficeActions.getPartners.type);
            return of(backOfficeActions.getPartnersError({ errorMessage }));
          })
        );
      })
    )
  );

  // ############
  // Users
  // ############

  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getUsers),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.getUsers(rest).pipe(
          map(users => {
            return backOfficeActions.getUsersSuccess({ users: users });
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.getUsers.type);
            return of(backOfficeActions.getUsersError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersActivateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersActivateAccount),
      switchMap(({ payload }) => {
        const { userID, password, passwordConfirm } = payload;
        return this.http.usersActivateAccount({ userID, password, passwordConfirm }).pipe(
          map(({ activatedAccountID }) => {
            return backOfficeActions.usersActivateAccountSuccess({ activatedAccountID });
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.usersActivateAccount.type);
            return of(backOfficeActions.usersActivateAccountError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersDeactivateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersDeactivateAccount),
      switchMap(({ payload }) => {
        const { userID } = payload;
        return this.http.usersDeactivateAccount({ userID }).pipe(
          map(({ deactivatedAccountID }) => {
            return backOfficeActions.usersDeactivateAccountSuccess({ deactivatedAccountID });
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.usersDeactivateAccount.type);
            return of(backOfficeActions.usersDeactivateAccountError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersRestoreAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersRestoreAccount),
      switchMap(({ payload }) => {
        const { userID } = payload;
        return this.http.usersRestoreAccount({ userID }).pipe(
          map(({ restoredAccountID }) => {
            return backOfficeActions.usersRestoreAccountSuccess({ restoredAccountID });
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.usersRestoreAccount.type);
            return of(backOfficeActions.usersRestoreAccountError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersChangePassword),
      switchMap(({ payload }) => {
        const { userID, mode, password, passwordConfirm } = payload;
        return this.http.usersChangePassword({ userID, mode, password, passwordConfirm }).pipe(
          map(BackOfficeChangePasswordReturn => {
            return backOfficeActions.usersChangePasswordSuccess(BackOfficeChangePasswordReturn);
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.usersChangePassword.type);
            return of(backOfficeActions.usersChangePasswordError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersSoftDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersSoftDelete),
      switchMap(({ payload }) => {
        const { usersID } = payload;
        return this.http.usersSoftDelete({ usersID }).pipe(
          map(({ deletedUsersID }) => {
            return backOfficeActions.usersSoftDeleteSuccess({ deletedUsersID });
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.usersSoftDelete.type);
            return of(backOfficeActions.usersSoftDeleteError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersPermDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersPermDelete),
      switchMap(({ payload }) => {
        const { usersID } = payload;
        return this.http.usersPermDelete({ usersID }).pipe(
          map(({ deletedUsersID }) => {
            return backOfficeActions.usersPermDeleteSuccess({ deletedUsersID });
          }),
          catchError(error => {
            const errorUsersMessage = parseError(error, backOfficeActions.usersPermDelete.type);
            return of(backOfficeActions.usersPermDeleteError({ errorUsersMessage }));
          })
        );
      })
    )
  );

  usersGenerateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.usersGenerateReport),
      switchMap(({ payload }) => {
        return this.http.usersGenerateReport(payload).pipe(
          map(response => {
            if (this.fileHandlerService.getFile(response)) {
              return backOfficeActions.usersGenerateReportSuccess();
            } else {
              return backOfficeActions.usersGenerateReportError({
                errorUsersMessage: 'DOWNLOADING ERROR',
              });
            }
          }),
          catchError(error => {
            const errorTransactionsMessage = parseError(error, backOfficeActions.transactionsGenerateReport.type);
            return of(backOfficeActions.transactionsGenerateReportError({ errorTransactionsMessage }));
          })
        );
      })
    )
  );

  // ############
  // Transactions
  // ############

  getTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getTransactions),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.getTransactions(rest).pipe(
          map(transactions => {
            return backOfficeActions.getTransactionsSuccess({ transactions: transactions });
          }),
          catchError(error => {
            const errorTransactionsMessage = parseError(error, backOfficeActions.getTransactions.type);
            return of(backOfficeActions.getTransactionsError({ errorTransactionsMessage }));
          })
        );
      })
    )
  );

  transactionsDownloadPurchaseDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.transactionsDownloadPurchaseDocument),
      mergeMap(({ transactionID, previewTargetKey }) => {
        return this.http.downloadPurchaseDocument({ transactionID }).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return backOfficeActions.transactionsDownloadPurchaseDocumentSuccess();
            } else {
              return backOfficeActions.transactionsDownloadPurchaseDocumentError({
                errorTransactionsMessage: 'DOWNLOADING ERROR',
              });
            }
          }),
          catchError(error => {
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            const errorTransactionsMessage = parseError(
              error,
              backOfficeActions.transactionsDownloadPurchaseDocument.type
            );
            return of(backOfficeActions.transactionsDownloadPurchaseDocumentError({ errorTransactionsMessage }));
          })
        );
      })
    )
  );

  transactionsGenerateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.transactionsGenerateReport),
      switchMap(({ payload }) => {
        return this.http.generateTransactionsReport(payload).pipe(
          map(response => {
            if (this.fileHandlerService.getFile(response)) {
              return backOfficeActions.transactionsGenerateReportSuccess();
            } else {
              return backOfficeActions.transactionsGenerateReportError({
                errorTransactionsMessage: 'DOWNLOADING ERROR',
              });
            }
          }),
          catchError(error => {
            const errorTransactionsMessage = parseError(error, backOfficeActions.transactionsGenerateReport.type);
            return of(backOfficeActions.transactionsGenerateReportError({ errorTransactionsMessage }));
          })
        );
      })
    )
  );

  // ############
  // Applications
  // ############

  getApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getApplications),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.getApplications(rest).pipe(
          map(applications => {
            return backOfficeActions.getApplicationsSuccess({ applications });
          }),
          catchError(error => {
            const errorApplicationsMessage = parseError(error, backOfficeActions.getApplications.type);
            return of(backOfficeActions.getApplicationsError({ errorApplicationsMessage }));
          })
        );
      })
    )
  );

  applicationsGenerateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.applicationsGenerateReport),
      switchMap(({ payload }) => {
        return this.http.generateApplicationsReport(payload).pipe(
          map(response => {
            if (this.fileHandlerService.getFile(response)) {
              return backOfficeActions.applicationsGenerateReportSuccess();
            } else {
              return backOfficeActions.applicationsGenerateReportError({
                errorApplicationsMessage: 'DOWNLOADING ERROR',
              });
            }
          }),
          catchError(error => {
            const errorApplicationsMessage = parseError(error, backOfficeActions.applicationsGenerateReport.type);
            return of(backOfficeActions.applicationsGenerateReportError({ errorApplicationsMessage }));
          })
        );
      })
    )
  );

  applicationsSoftDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.applicationsSoftDelete),
      switchMap(({ payload }) => {
        return this.http.applicationsSoftDelete(payload).pipe(
          map(({ deletedApplicationsID }) => {
            return backOfficeActions.applicationsSoftDeleteSuccess({ deletedApplicationsID });
          }),
          catchError(error => {
            const errorApplicationsMessage = parseError(error, backOfficeActions.applicationsSoftDelete.type);
            return of(backOfficeActions.applicationsSoftDeleteError({ errorApplicationsMessage }));
          })
        );
      })
    )
  );

  applicationsRestore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.applicationsRestore),
      switchMap(({ payload }) => {
        return this.http.applicationsRestore(payload).pipe(
          map(({ deletedApplicationID }) => {
            return backOfficeActions.applicationsRestoreSuccess({ deletedApplicationID });
          }),
          catchError(error => {
            const errorApplicationsMessage = parseError(error, backOfficeActions.applicationsRestore.type);
            return of(backOfficeActions.applicationsRestoreeError({ errorApplicationsMessage }));
          })
        );
      })
    )
  );

  scheduleUserProcessesFilesToDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.scheduleUserProcessesFilesToDelete),
      switchMap(({ payload }) => {
        return this.http.scheduleUserProcessesFilesToDelete(payload).pipe(
          map(({ scheduledUserProcessesFilesToDelete }) => {
            return backOfficeActions.scheduleUserProcessesFilesToDeleteSuccess({ scheduledUserProcessesFilesToDelete });
          }),
          catchError(error => {
            const errorApplicationsMessage = parseError(
              error,
              backOfficeActions.scheduleUserProcessesFilesToDelete.type
            );
            return of(backOfficeActions.scheduleUserProcessesFilesToDeleteError({ errorApplicationsMessage }));
          })
        );
      })
    )
  );

  // ############
  // SCHEDULED FILES DELETION
  // ############

  getScheduledFilesDeletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getScheduledFilesDeletion),
      switchMap(({ payload }) => {
        const { all, count, ...rest } = payload;
        return this.http.getScheduledFilesDeletion(rest).pipe(
          map(scheduledFilesDeletion => {
            return backOfficeActions.getScheduledFilesDeletionSuccess({ scheduledFilesDeletion });
          }),
          catchError(error => {
            const errorScheduledFilesDeletionMessage = parseError(
              error,
              backOfficeActions.getScheduledFilesDeletion.type
            );
            return of(backOfficeActions.getScheduledFilesDeletionError({ errorScheduledFilesDeletionMessage }));
          })
        );
      })
    )
  );

  deleteScheduledFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.deleteScheduledFiles),
      switchMap(({ payload }) => {
        return this.http.deleteScheduledFiles(payload).pipe(
          map(({ scheduledFileDeletion }) => {
            return backOfficeActions.deleteScheduledFilesSuccess({ scheduledFileDeletion });
          }),
          catchError(error => {
            const errorScheduledFilesDeletionMessage = parseError(error, backOfficeActions.deleteScheduledFiles.type);
            return of(backOfficeActions.deleteScheduledFilesError({ errorScheduledFilesDeletionMessage }));
          })
        );
      })
    )
  );

  restoreScheduledFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.restoreScheduledFiles),
      switchMap(({ payload }) => {
        return this.http.restoreScheduledFiles(payload).pipe(
          map(({ scheduledFileDeletion }) => {
            return backOfficeActions.restoreScheduledFilesSuccess({ scheduledFileDeletion });
          }),
          catchError(error => {
            const errorScheduledFilesDeletionMessage = parseError(error, backOfficeActions.restoreScheduledFiles.type);
            return of(backOfficeActions.restoreScheduledFilesError({ errorScheduledFilesDeletionMessage }));
          })
        );
      })
    )
  );

  // ############
  // MANAGE FILES
  // ############

  getUserProcessBasicInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getUserProcessBasicInformation),
      switchMap(({ userProcessId }) => {
        return this.http.getUserProcessBasicInformation(userProcessId).pipe(
          map(({ userProcess }) => {
            return backOfficeActions.getUserProcessBasicInformationSuccess({ userProcess });
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.getUserProcessBasicInformation.type);
            return of(backOfficeActions.getUserProcessBasicInformationError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );

  getUserProcessManageAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.getUserProcessManageFiles),
      switchMap(({ userProcessId }) => {
        return this.http.getUserProcessManageFiles(userProcessId).pipe(
          map(userProcessFiles => {
            return backOfficeActions.getUserProcessManageFilesSuccess({ userProcessFiles });
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.getUserProcessManageFiles.type);
            return of(backOfficeActions.getUserProcessManageFilesError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );

  manageFilesDownload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.manageFilesDownload),
      switchMap(({ payload }) => {
        return this.http.manageFilesDownload(payload).pipe(
          map(file => {
            const newWindow = window.open();

            newWindow.addEventListener('DOMContentLoaded', () => (newWindow.document.title = payload.fileName));
            newWindow.document.write(`
								<style>body {margin: 0 !important;}</style>
								<iframe src="${URL.createObjectURL(
                  file
                )}" frameborder="0" style="border:0; top:0; left:0; bottom:0; right:0; width:100%; height:100%;" allowfullscreen>
								</iframe>
              `);
            newWindow.document.title = payload.fileName;
            return backOfficeActions.manageFilesDownloadSuccess();
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.manageFilesDownload.type);
            return of(backOfficeActions.manageFilesDownloadError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );

  manageFilesDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.manageFilesScheduleToDelete),
      switchMap(({ payload }) => {
        return this.http.manageFilesDelete(payload).pipe(
          map(({ fileId, fileName, fileType }) => {
            return backOfficeActions.manageFilesScheduleToDeleteSuccess({ fileId, fileName, fileType });
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.manageFilesScheduleToDelete.type);
            return of(backOfficeActions.manageFilesScheduleToDeleteError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );

  manageFilesDeleteAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.manageFilesScheduleToDeleteAll),
      switchMap(({ payload }) => {
        return this.http.manageFilesDeleteAll(payload).pipe(
          map(({ scheduledFilesDeletion }) => {
            return backOfficeActions.manageFilesScheduleToDeleteAllSuccess({ scheduledFilesDeletion });
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.manageFilesScheduleToDeleteAll.type);
            return of(backOfficeActions.manageFilesScheduleToDeleteAllError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );

  manageFilesRestore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.manageFilesRestore),
      switchMap(({ payload }) => {
        return this.http.manageFilesRestore(payload).pipe(
          map(({ fileId, fileName, fileType }) => {
            return backOfficeActions.manageFilesRestoreSuccess({ fileId, fileName, fileType });
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.manageFilesRestore.type);
            return of(backOfficeActions.manageFilesRestoreError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );

  manageFilesRestoreAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(backOfficeActions.manageFilesRestoreAll),
      switchMap(({ payload }) => {
        return this.http.manageFilesRestoreAll(payload).pipe(
          map(({ success, deletedCount }) => {
            return backOfficeActions.manageFilesRestoreAllSuccess({ success, deletedCount });
          }),
          catchError(error => {
            const errorManageFilesMessage = parseError(error, backOfficeActions.manageFilesRestoreAll.type);
            return of(backOfficeActions.manageFilesRestoreAllError({ errorManageFilesMessage }));
          })
        );
      })
    )
  );
}
